import { useState, type FormEvent } from 'react'

type SubmitState = 'idle' | 'submitting' | 'success' | 'error'

export function ContactForm() {
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [feedback, setFeedback] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)

    setSubmitState('submitting')
    setFeedback('')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.get('name'),
          email: formData.get('email'),
          company: formData.get('company'),
          message: formData.get('message'),
          website: formData.get('website'),
        }),
      })
      const result = await response.json().catch(() => null) as { message?: string } | null

      if (!response.ok) throw new Error(result?.message || 'We could not send your message. Please try again.')

      form.reset()
      setSubmitState('success')
      setFeedback(result?.message || 'Thanks — your message has been received.')
    } catch (error) {
      setSubmitState('error')
      setFeedback(error instanceof Error ? error.message : 'We could not send your message. Please try again.')
    }
  }

  return <section className="contact-section" id="contact">
    <div className="contact-intro">
      <p className="eyebrow"><span aria-hidden="true" /> Talk to a human</p>
      <h2>Have a catalog problem worth fixing?</h2>
      <p>Tell us what is slowing your team down. We read every message and will reply from <strong>catalogfix.team@gmail.com</strong>.</p>
      <div className="contact-promises"><span>No sales bot</span><span>No catalog upload</span><span>One practical reply</span></div>
    </div>
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="contact-form-row">
        <label><span>Name *</span><input name="name" type="text" autoComplete="name" minLength={2} maxLength={100} required /></label>
        <label><span>Email *</span><input name="email" type="email" autoComplete="email" maxLength={254} required /></label>
      </div>
      <label><span>Company <small>optional</small></span><input name="company" type="text" autoComplete="organization" maxLength={120} /></label>
      <label><span>What would you like to fix? *</span><textarea name="message" minLength={10} maxLength={4000} rows={5} required placeholder="For example: supplier files, Google feed errors, or a multi-store catalog workflow…" /></label>
      <label className="contact-honeypot" aria-hidden="true">Website<input name="website" type="text" tabIndex={-1} autoComplete="off" /></label>
      <div className="contact-form-footer">
        <button className="button primary" type="submit" disabled={submitState === 'submitting'}>{submitState === 'submitting' ? 'Sending…' : 'Send message'} <span aria-hidden="true">↗</span></button>
        <p>By sending this form, you agree to our <a href="/privacy">Privacy Policy</a>.</p>
      </div>
      {feedback && <p className={`contact-feedback ${submitState}`} role="status" aria-live="polite">{feedback}</p>}
    </form>
  </section>
}
